import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FaBriefcase, FaCode, FaEnvelope, FaGithub, FaHome, FaSearch } from "react-icons/fa";
import { BsFillPersonLinesFill } from "react-icons/bs";

const scrollToSection = (sectionName) => {
  const target = document.querySelector(`section[name="${sectionName}"]`);
  target?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const CommandPalette = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const commands = useMemo(
    () => [
      { label: "Go to Home", hint: "Intro and hero", icon: <FaHome />, action: () => scrollToSection("Home") },
      { label: "View Portfolio", hint: "Projects and case studies", icon: <FaBriefcase />, action: () => scrollToSection("Portfolio") },
      { label: "Explore Skills", hint: "Tech stack", icon: <FaCode />, action: () => scrollToSection("Skills") },
      { label: "Contact Aakash", hint: "Send a message", icon: <FaEnvelope />, action: () => scrollToSection("Contact") },
      { label: "Open GitHub", hint: "github.com/AakashJha04", icon: <FaGithub />, action: () => window.open("https://github.com/AakashJha04", "_blank", "noreferrer") },
      { label: "Download Resume", hint: "PDF resume", icon: <BsFillPersonLinesFill />, action: () => window.open("/resume.pdf", "_blank", "noreferrer") },
    ],
    []
  );

  const filteredCommands = commands.filter((command) =>
    `${command.label} ${command.hint}`.toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((prev) => !prev);
      }

      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const runCommand = (command) => {
    command.action();
    setOpen(false);
    setQuery("");
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="pill-button fixed bottom-6 left-6 z-[70] hidden lg:flex"
      >
        <FaSearch className="text-indigo-500" />
        Quick actions
        <span className="rounded-full border border-slate-200 px-2 py-0.5 text-[11px] text-slate-500 dark:border-zinc-700 dark:text-zinc-400">
          Ctrl K
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] bg-slate-950/50 px-4 py-20"
            onMouseDown={() => setOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.18 }}
              className="panel mx-auto max-w-2xl p-4"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="relative">
                <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search actions, sections, resume, GitHub..."
                  className="w-full rounded-xl border border-slate-200 bg-white py-3.5 pl-11 pr-4 text-sm font-medium text-slate-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-400/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                />
              </div>

              <div className="mt-3 grid gap-1">
                {filteredCommands.map((command) => (
                  <button
                    key={command.label}
                    type="button"
                    onClick={() => runCommand(command)}
                    className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-slate-50 dark:hover:bg-zinc-800"
                  >
                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400">
                      {command.icon}
                    </span>
                    <span>
                      <span className="block text-sm font-semibold text-slate-800 dark:text-zinc-100">
                        {command.label}
                      </span>
                      <span className="block text-xs text-slate-500 dark:text-zinc-400">
                        {command.hint}
                      </span>
                    </span>
                  </button>
                ))}

                {filteredCommands.length === 0 && (
                  <p className="p-5 text-center text-sm font-medium text-slate-500 dark:text-zinc-400">
                    No matching action found.
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CommandPalette;
